import React from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import {
    FormControl,
    Select,
    MenuItem,
} from '@mui/material';
import './BarChartCard.css';

/**
 * BarChartCard component.
 *
 * A dashboard card displaying a MUI BarChart with three structured rows:
 * 1. **Title row** — optional card heading passed via `title`.
 * 2. **Filters row** — optional year picker rendered when `yearOptions` is
 *    non-empty.
 * 3. **Chart area** — a responsive MUI BarChart that fills the card width.
 *
 * Visual style matches the StatCard: white background, 16 px border-radius,
 * subtle shadow.
 *
 * @component
 * @param {object}   props
 * @param {string}   [props.title]              - Optional card title shown in the first row.
 * @param {string[]} [props.yearOptions=[]]    - Year strings for the year dropdown.
 *                                               Omitting or passing `[]` hides the filters row.
 * @param {string}   [props.selectedYear='']   - Currently selected year value.
 * @param {Function} [props.onYearChange]      - Called with the MUI Select change event.
 * @param {(number|null)[]} [props.data=[]]    - Series data values (12 entries for monthly charts;
 *                                               `null` entries render as gaps).
 * @param {string[]} [props.xAxisLabels=[]]    - Labels for the x-axis bands (e.g. month names).
 * @param {string}   [props.yAxisLabel='']     - Rotated label shown on the y-axis.
 * @param {string}   [props.barColor='#fc9f5b'] - Fill colour applied to all bars.
 * @param {string}   [props.noDataMessage]     - Message shown by the chart when `data` is empty.
 * @param {boolean}  [props.loading=false]     - When `true` a shimmer skeleton replaces the chart.
 * @returns {JSX.Element}
 */
const BarChartCard = ({
    title,
    yearOptions = [],
    selectedYear = '',
    onYearChange,
    data = [],
    xAxisLabels = [],
    yAxisLabel = '',
    barColor = '#fc9f5b',
    noDataMessage = 'No data',
    loading = false,
}) => {
    return (
        <div className="bcc-card">

            {/* ── Row 1: title ── */}
            {title && <span className="bcc-title">{title}</span>}

            {/* ── Row 2: filters ── */}
            {yearOptions.length > 0 && (
                <div className="bcc-filters">
                    <FormControl size="small">
                        <Select
                            value={selectedYear}
                            onChange={onYearChange}
                            displayEmpty
                            sx={{
                                fontSize: '0.82rem',
                                borderRadius: '8px',
                                color: '#374151',
                                minWidth: 110,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e5e7eb',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#fc9f5b',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#fc9f5b',
                                    borderWidth: '1px',
                                },
                            }}
                        >
                            {yearOptions.map(y => (
                                <MenuItem key={y} value={y} sx={{ fontSize: '0.82rem' }}>
                                    {y}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            )}

            {/* ── Row 3: chart ── */}
            <div className="bcc-chart">
                {loading ? (
                    <div className="bcc-skeleton" />
                ) : (
                    <BarChart
                        xAxis={[{
                            scaleType: 'band',
                            data: xAxisLabels,
                            // Keep bar width consistent regardless of how many bars there are.
                            // 0.08 * n gives the natural bar fraction at n=10 with default 0.2 gap;
                            // clamped to 0.2 so we never go narrower than the default.
                            categoryGapRatio: Math.max(0.2, 1 - 0.08 * data.length),
                        }]}
                        yAxis={[{
                            id: 'y',
                            scaleType: 'linear',
                            label: yAxisLabel,
                            labelStyle: {
                                transform: 'rotate(-90deg) translate(-15%, -60%)',
                                textAnchor: 'end',
                            },
                        }]}
                        series={[{ yAxisId: 'y', data }]}
                        leftAxis="y"
                        barLabel="value"
                        colors={[barColor]}
                        height={320}
                        margin={{ left: 70, right: 20 }}
                        slotProps={{
                            noDataOverlay: { message: noDataMessage },
                        }}
                    />
                )}
            </div>

        </div>
    );
};

export default BarChartCard;
